import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useRef,
  useMemo
} from "react";
import { toast } from "react-toastify";
import * as mainframeApi from "./mainframeApi";
import useMcpToken from "../../hooks/useMcpToken";
import { readSseStream } from "./utils";
import agentChatReducer, {
  initialState,
  ActionTypes
} from "./agentChatReducer";

const AgentChatContext = createContext(null);

const MAX_CONCURRENT_STREAMS = 5;

function createMessage(role, content, extra = {}) {
  return {
    id: crypto.randomUUID(),
    role,
    content,
    created_at: new Date().toISOString(),
    ...extra
  };
}

export function AgentChatProvider({
  children,
  apiBasePath = "/api/analytics"
}) {
  const { token, tokenError } = useMcpToken();
  const [state, dispatch] = useReducer(agentChatReducer, initialState);

  const stateRef = useRef(state);
  stateRef.current = state;

  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  function safeDispatch(action) {
    if (mountedRef.current) dispatch(action);
  }

  // Fetch session list on mount
  useEffect(() => {
    if (!token) {
      if (tokenError) {
        safeDispatch({ type: ActionTypes.SESSION_LIST_LOAD_FAILED });
      }
      return;
    }

    async function loadSessionList() {
      try {
        const data = await mainframeApi.fetchSessions(token, apiBasePath);
        safeDispatch({
          type: ActionTypes.SESSION_LIST_LOADED,
          sessions: data.sessions || []
        });
      } catch {
        toast.error("Failed to load sessions");
        safeDispatch({ type: ActionTypes.SESSION_LIST_LOAD_FAILED });
      }
    }

    loadSessionList();
  }, [token, tokenError, apiBasePath]);

  const selectSession = useCallback(
    async (sessionId) => {
      dispatch({ type: ActionTypes.SESSION_SELECTED, sessionId });

      if (!token || stateRef.current.openedSessions[sessionId]) return;

      try {
        const data = await mainframeApi.fetchSession(
          token,
          apiBasePath,
          sessionId
        );
        safeDispatch({
          type: ActionTypes.SESSION_MESSAGES_LOADED,
          sessionId,
          messages: data.messages || []
        });
      } catch {
        toast.error("Failed to load session");
        safeDispatch({
          type: ActionTypes.SESSION_MESSAGES_LOAD_FAILED,
          sessionId
        });
      }
    },
    [token, apiBasePath]
  );

  const createSession = useCallback(async () => {
    if (!token) return null;

    dispatch({ type: ActionTypes.SESSION_CREATING });
    try {
      const newSession = await mainframeApi.createSession(
        token,
        apiBasePath,
        {}
      );
      safeDispatch({ type: ActionTypes.SESSION_CREATED, session: newSession });
      return newSession;
    } catch {
      toast.error("Failed to create session");
      safeDispatch({ type: ActionTypes.SESSION_CREATE_FAILED });
      return null;
    }
  }, [token, apiBasePath]);

  const deleteSession = useCallback(
    async (sessionId) => {
      if (!token) return;

      try {
        await mainframeApi.deleteSession(token, apiBasePath, sessionId);
        safeDispatch({ type: ActionTypes.SESSION_DELETED, sessionId });
      } catch {
        toast.error("Failed to delete session");
      }
    },
    [token, apiBasePath]
  );

  const sendMessage = useCallback(
    async (sessionId, message) => {
      const sessions = stateRef.current.openedSessions;
      const streamingCount = Object.values(sessions).filter(
        (s) => s.status === "streaming"
      ).length;

      if (sessions[sessionId]?.status === "streaming") return;

      if (streamingCount >= MAX_CONCURRENT_STREAMS) {
        toast.warn("Too many messages in progress. Please wait.");
        return;
      }

      const userMsg = createMessage("user", message);
      dispatch({
        type: ActionTypes.MESSAGE_SENT,
        sessionId,
        message: userMsg
      });

      const truncated =
        message.slice(0, 100) + (message.length > 100 ? "..." : "");
      dispatch({
        type: ActionTypes.SESSION_TITLE_SET,
        sessionId,
        title: truncated
      });

      try {
        const response = await mainframeApi.sendMessage(
          token,
          apiBasePath,
          sessionId,
          message
        );

        for await (const event of readSseStream(response)) {
          if (!mountedRef.current) break;

          switch (event.type) {
            case "summary": {
              const msg = createMessage("assistant", event.data.summary, {
                analytics_query_id: event.data.query_id
              });
              safeDispatch({
                type: ActionTypes.SSE_SUMMARY_RECEIVED,
                sessionId,
                message: msg
              });
              break;
            }
            case "error": {
              const msg = createMessage(
                "assistant",
                `Error: ${event.data.message}`
              );
              safeDispatch({
                type: ActionTypes.SSE_ERROR_RECEIVED,
                sessionId,
                message: msg
              });
              break;
            }
            case "status":
              safeDispatch({
                type: ActionTypes.SSE_STATUS_RECEIVED,
                sessionId,
                statusMessage: event.data.message
              });
              break;
            default:
              break;
          }
        }
      } catch (err) {
        const errorMsg = createMessage("assistant", `Error: ${err.message}`);
        safeDispatch({
          type: ActionTypes.SSE_ERROR_RECEIVED,
          sessionId,
          message: errorMsg
        });
      } finally {
        safeDispatch({ type: ActionTypes.STREAM_COMPLETED, sessionId });
      }
    },
    [token, apiBasePath]
  );

  const activeSession = state.openedSessions[state.activeSessionId];

  const value = useMemo(
    () => ({
      sessionList: state.sessionList,
      activeSessionId: state.activeSessionId,
      activeSession,
      isLoadingList: state.isLoadingList,
      isCreating: state.isCreating,
      token,

      selectSession,
      createSession,
      deleteSession,
      sendMessage
    }),
    [
      state.sessionList,
      state.activeSessionId,
      activeSession,
      state.isLoadingList,
      state.isCreating,
      token,
      selectSession,
      createSession,
      deleteSession,
      sendMessage
    ]
  );

  return (
    <AgentChatContext.Provider value={value}>
      {children}
    </AgentChatContext.Provider>
  );
}

export function useAgentChat() {
  const ctx = useContext(AgentChatContext);
  if (!ctx)
    throw new Error("useAgentChat must be used within AgentChatProvider");
  return ctx;
}
